import { useState, useEffect } from 'react';
import * as analyticsApi from '../../api/analytics';

/**
 * RelatedFieldMapping - Component for configuring lookup field resolution
 *
 * Allows users to map file columns to CRM lookup fields by specifying:
 * - The lookup field (e.g., AccountId)
 * - The related object (e.g., Account)
 * - The matching field on the related object (e.g., Name)
 * - The file column containing the lookup value (e.g., Company Name)
 * - Behavior for "not found" scenarios
 * - Behavior for "multiple found" scenarios
 *
 * Example: To map a "Company Name" column to Contact.AccountId:
 * - Lookup field: AccountId (on Contact)
 * - Related object: Account
 * - Matching field: Name (on Account)
 * - File column: Company Name
 * - Query: SELECT Id FROM Account WHERE Name = '{Company Name}'
 */
function RelatedFieldMapping({
  fileId,
  objectName,
  sourceType = 'salesforce',
  dataSourceId,
  lookupMappings = [],
  onLookupMappingsChange,
}) {
  // State
  const [availableFields, setAvailableFields] = useState([]);
  const [relatedObjects, setRelatedObjects] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [lookups, setLookups] = useState(lookupMappings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState(null);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load available fields for the object
        const fieldsResponse = await analyticsApi.fetchFieldsByObject(objectName);
        const fields = fieldsResponse.fields || [];
        setAvailableFields(fields);

        // Filter lookup/relationship fields
        const lookupFields = fields.filter(f =>
          f.type === 'reference' ||
          f.type === 'lookup' ||
          f.type === 'master-detail'
        );

        // Extract related objects from lookup fields
        const relatedObjSet = new Set();
        lookupFields.forEach(field => {
          if (field.related_object) {
            relatedObjSet.add(field.related_object);
          }
        });
        setRelatedObjects(Array.from(relatedObjSet));

        // Load file columns
        const preview = await analyticsApi.getFilePreview(fileId);
        setFileColumns(preview.column_headers || []);

        // Initialize lookups if empty
        if (lookups.length === 0 && lookupMappings.length === 0) {
          setLookups([]);
        }
      } catch (err) {
        console.error('Failed to load lookup mapping data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (fileId && objectName) {
      loadData();
    }
  }, [fileId, objectName]);

  // Notify parent when lookups change
  useEffect(() => {
    if (onLookupMappingsChange) {
      onLookupMappingsChange(lookups);
    }
  }, [lookups, onLookupMappingsChange]);

  // Add new lookup mapping
  const handleAddLookup = () => {
    setLookups(prev => [...prev, {
      lookup_field: '',
      related_object: '',
      matching_field: '',
      file_column: '',
      not_found_behavior: 'error', // error, skip, blank
      multiple_found_behavior: 'first', // error, first, skip
    }]);
  };

  // Remove lookup mapping
  const handleRemoveLookup = (index) => {
    setLookups(prev => prev.filter((_, i) => i !== index));
  };

  // Update lookup mapping
  const handleLookupChange = (index, field, value) => {
    setLookups(prev => prev.map((lookup, i) => {
      if (i === index) {
        const updated = { ...lookup, [field]: value };

        // Auto-populate related object when lookup field is selected
        if (field === 'lookup_field' && value) {
          const fieldInfo = availableFields.find(f => f.api_name === value);
          if (fieldInfo && fieldInfo.related_object) {
            updated.related_object = fieldInfo.related_object;
          }
        }

        return updated;
      }
      return lookup;
    }));
  };

  // Preview lookup resolution
  const handlePreviewLookup = async (index) => {
    const lookup = lookups[index];
    if (!lookup.lookup_field || !lookup.related_object || !lookup.matching_field || !lookup.file_column) {
      setError('All fields must be filled to preview lookup');
      return;
    }

    setPreviewLoading(true);
    setError(null);
    setPreviewResults(null);

    try {
      // Get sample values from file
      const preview = await analyticsApi.getFilePreview(fileId);
      const columnIndex = preview.column_headers.indexOf(lookup.file_column);

      if (columnIndex === -1) {
        throw new Error('File column not found in preview');
      }

      // Get first 5 unique values
      const sampleValues = [];
      const seenValues = new Set();
      for (const row of preview.sample_rows) {
        const value = row[columnIndex];
        if (value && !seenValues.has(value) && sampleValues.length < 5) {
          sampleValues.push(value);
          seenValues.add(value);
        }
      }

      // Preview results (in a real implementation, this would call the backend)
      // For now, we'll just show the configuration
      setPreviewResults({
        lookup_field: lookup.lookup_field,
        related_object: lookup.related_object,
        matching_field: lookup.matching_field,
        sample_values: sampleValues,
        query_template: `SELECT Id FROM ${lookup.related_object} WHERE ${lookup.matching_field} = '{value}'`,
      });
    } catch (err) {
      console.error('Failed to preview lookup:', err);
      setError(err.message || 'Failed to preview lookup');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="ml-3 text-gray-600">Loading lookup configuration...</p>
      </div>
    );
  }

  // Get lookup fields only
  const lookupFields = availableFields.filter(f =>
    f.type === 'reference' ||
    f.type === 'lookup' ||
    f.type === 'master-detail'
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Related Field Lookups</h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure how lookup fields should be resolved from file values
          </p>
        </div>
        <button
          onClick={handleAddLookup}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Lookup
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* No lookups message */}
      {lookups.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No lookup mappings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add a lookup mapping to resolve related records from file values
          </p>
        </div>
      )}

      {/* Lookup mappings */}
      {lookups.map((lookup, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4 bg-white shadow-sm">
          {/* Header with remove button */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">Lookup #{index + 1}</h4>
            <button
              onClick={() => handleRemoveLookup(index)}
              className="text-red-600 hover:text-red-700 transition"
              title="Remove lookup"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Lookup Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lookup Field <span className="text-red-500">*</span>
              </label>
              <select
                value={lookup.lookup_field}
                onChange={(e) => handleLookupChange(index, 'lookup_field', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">-- Select lookup field --</option>
                {lookupFields.map((field) => (
                  <option key={field.api_name} value={field.api_name}>
                    {field.label} ({field.api_name})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The lookup field on {objectName} (e.g., AccountId)
              </p>
            </div>

            {/* Related Object */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Related Object <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={lookup.related_object}
                onChange={(e) => handleLookupChange(index, 'related_object', e.target.value)}
                placeholder="e.g., Account"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-gray-50"
                readOnly
              />
              <p className="mt-1 text-xs text-gray-500">
                Auto-filled from lookup field
              </p>
            </div>

            {/* Matching Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matching Field <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={lookup.matching_field}
                onChange={(e) => handleLookupChange(index, 'matching_field', e.target.value)}
                placeholder="e.g., Name, Email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Field on {lookup.related_object || 'related object'} to match against
              </p>
            </div>

            {/* File Column */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File Column <span className="text-red-500">*</span>
              </label>
              <select
                value={lookup.file_column}
                onChange={(e) => handleLookupChange(index, 'file_column', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">-- Select file column --</option>
                {fileColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Column containing the lookup value
              </p>
            </div>

            {/* Not Found Behavior */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                If Not Found
              </label>
              <select
                value={lookup.not_found_behavior}
                onChange={(e) => handleLookupChange(index, 'not_found_behavior', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="error">Error (fail operation)</option>
                <option value="skip">Skip (ignore record)</option>
                <option value="blank">Blank (leave field empty)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                What to do when no matching record is found
              </p>
            </div>

            {/* Multiple Found Behavior */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                If Multiple Found
              </label>
              <select
                value={lookup.multiple_found_behavior}
                onChange={(e) => handleLookupChange(index, 'multiple_found_behavior', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="error">Error (fail operation)</option>
                <option value="first">First (use first match)</option>
                <option value="skip">Skip (ignore record)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                What to do when multiple matching records are found
              </p>
            </div>
          </div>

          {/* Preview button */}
          <div className="flex justify-end">
            <button
              onClick={() => handlePreviewLookup(index)}
              disabled={previewLoading || !lookup.lookup_field || !lookup.matching_field || !lookup.file_column}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {previewLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                  Previewing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </>
              )}
            </button>
          </div>

          {/* Preview results */}
          {previewResults && previewResults.lookup_field === lookup.lookup_field && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-blue-900 mb-2">Preview</h5>
              <div className="text-sm text-blue-800 space-y-2">
                <div>
                  <span className="font-medium">Query:</span>
                  <code className="block mt-1 p-2 bg-white rounded text-xs font-mono">
                    {previewResults.query_template}
                  </code>
                </div>
                <div>
                  <span className="font-medium">Sample values from file:</span>
                  <ul className="list-disc list-inside mt-1 pl-2">
                    {previewResults.sample_values.map((val, i) => (
                      <li key={i}>{val}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Info box */}
      {lookups.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">How Lookup Resolution Works</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p>1. For each record, the system will query the CRM to find matching related records</p>
            <p>2. The lookup field will be populated with the Id of the matching record</p>
            <p>3. If no match or multiple matches are found, the configured behavior will be applied</p>
            <p>4. Lookup values are cached to minimize API calls for repeated values</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default RelatedFieldMapping;
